import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useThrottleFn } from "ahooks";
import { getImageSrc } from "../../lang/imgMap";
import "./index.scss";

interface ScrollAnchorProps {
  lang: string;
  sections?: string[];
}

const useScrollAnchor = (sections: string[]) => {
  const [activeSection, setActiveSection] = useState(
    sections[0] || "section-1"
  );
  const sectionElementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const isScrollingRef = useRef(false);

  // 缓存元素引用
  useEffect(() => {
    const elementsMap = new Map<string, HTMLElement>();
    sections.forEach((section) => {
      const element = document.getElementById(section);
      if (element) {
        elementsMap.set(section, element);
      }
    });
    sectionElementsRef.current = elementsMap;
  }, [sections]);

  // 优化的滚动处理函数
  const handleScroll = useCallback(() => {
    if (isScrollingRef.current) return;

    const scrollContainer = document.querySelector(".home") as HTMLElement;
    const elementsMap = sectionElementsRef.current;

    if (scrollContainer) {
      // 使用容器滚动
      const scrollPosition =
        scrollContainer.scrollTop + scrollContainer.clientHeight / 2;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = elementsMap.get(section);

        if (element) {
          const elementTop = element.offsetTop;

          if (elementTop <= scrollPosition) {
            setActiveSection((prev) => (prev !== section ? section : prev));
            break;
          }
        }
      }
    } else {
      // 使用window滚动
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = elementsMap.get(section);

        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;

          if (elementTop <= scrollPosition) {
            setActiveSection((prev) => (prev !== section ? section : prev));
            break;
          }
        }
      }
    }
  }, [sections]);

  // 使用 ahooks 的节流函数
  const { run: throttledHandleScroll } = useThrottleFn(handleScroll, {
    wait: 16, // 约60fps
  });

  useEffect(() => {
    const scrollContainer = document.querySelector(".home");
    const targetElement = scrollContainer || window;

    targetElement.addEventListener("scroll", throttledHandleScroll, {
      passive: true,
    });
    handleScroll(); // 初始化

    return () => {
      targetElement.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [throttledHandleScroll, handleScroll]);

  return { activeSection, setActiveSection };
};

// 自定义 hook：管理锚点图片
const useAnchorImages = (lang: string, sections: string[]) => {
  const [anchorImages, setAnchorImages] = useState<{ [key: string]: string }>(
    {}
  );

  useEffect(() => {
    const imageMap: { [key: string]: string } = {};

    sections.forEach((section, index) => {
      try {
        const pageNum = `p${index + 1}`;
        const image = getImageSrc(lang, `pc/${pageNum}/anchor.png`);
        imageMap[section] = image;
      } catch (error) {
        console.warn(
          `Failed to load anchor image for section ${section}:`,
          error
        );
        imageMap[section] = "";
      }
    });

    setAnchorImages(imageMap);
  }, [lang, sections]);

  return anchorImages;
};

const ScrollAnchor: React.FC<ScrollAnchorProps> = ({
  lang,
  sections: propSections,
}) => {
  const sections = useMemo(
    () => propSections || ["section-1", "section-2", "section-3"],
    [propSections]
  );

  const { activeSection } = useScrollAnchor(sections);
  const anchorImages = useAnchorImages(lang, sections);

  const onScrollToSection = useCallback((section: string) => {
    const element = document.getElementById(section);
    if (!element) {
      console.error("未找到元素:", section);
      return;
    }

    try {
      const scrollContainer = document.querySelector(".home") as HTMLElement;

      if (scrollContainer) {
        // 计算元素相对于滚动容器的位置
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // 计算需要滚动的距离
        const scrollTop =
          elementRect.top - containerRect.top + scrollContainer.scrollTop;

        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      } else {
        // 使用window滚动
        const rect = element.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const elementTop = rect.top + scrollTop;

        window.scrollTo({
          top: elementTop,
          behavior: "smooth",
        });
      }
    } catch (error) {
      console.error("滚动到section时出错:", error);
    }
  }, []);

  return (
    <div className='scroll-anchor' role='navigation' aria-label='页面导航'>
      {sections.map((section) => {
        const isActive = activeSection === section;
        const imageSrc = anchorImages[section];

        return (
          <img
            key={section}
            src={imageSrc || ""}
            style={{
              opacity: isActive ? 1 : 0,
              display: isActive ? "block" : "none",
            }}
            loading='lazy'
          />
        );
      })}
      {sections.map((section, index) => (
        <div
          key={section}
          className={`scroll-anchor-line line${index + 1} ${
            activeSection === section ? "active" : ""
          }`}
          onClick={() => onScrollToSection(section)}
          tabIndex={0}
        />
      ))}
    </div>
  );
};

export default ScrollAnchor;
